import axios from "axios";
import { Field, Form, withFormik } from "formik";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardGroup,
  CardHeader,
  CardTitle,
  Col,
  Container,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import {
  API_BASE_URL,
  BASE_LOGIN_REDIRECT_URL,
} from "../../constants/api.constants";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import { saveLoginEmailPrefill } from "../../Utilities/loginEmailPrefill";

const LEGACY_EMAIL_TIMEOUT_MS = 5000;

const RequestResetPasswordForm = (props) => {
  const {
    touched,
    errors,
    isSubmitting,
    status,
    setStatus,
    setFieldValue,
    values,
    legacyCheck,
  } = props;

  const [isFetchingEmail, setIsFetchingEmail] = useState(
    Boolean(props.activation && legacyCheck),
  );

  // En gammal aktiveringslänk bär adressen krypterad. Servern läser ut den, så formuläret kan fyllas
  // i. Fältet är låst medan det pågår, så att inget som skrivits hinner skrivas över. Misslyckas det
  // eller dröjer det görs inget mer än att låsa upp fältet: personen skriver adressen själv.
  useEffect(() => {
    if (!props.activation || !legacyCheck) {
      return;
    }

    axios
      .post(
        `${API_BASE_URL}/password/legacyemail`,
        { check: legacyCheck },
        { timeout: LEGACY_EMAIL_TIMEOUT_MS },
      )
      .then((res) => setFieldValue("email", res.data.email))
      .catch(() => {})
      .finally(() => setIsFetchingEmail(false));
  }, [props.activation, legacyCheck, setFieldValue]);

  const redirectUrl = () => {
    if (status === "sent") {
      saveLoginEmailPrefill(values.email);
    }

    window.location.replace(`${BASE_LOGIN_REDIRECT_URL}/`);
  };

  return (
    <div className="app flex-row align-items-center">
      <Container>
        <Row className="justify-content-center">
          <Col md="8">
            <Form>
              <CardGroup>
                <Card
                  style={{ cursor: "pointer", width: "44%" }}
                  body
                  className="text-white bg-success py-5 d-md-down-none "
                >
                  <div onClick={redirectUrl} title="Tillbaka till loginsidan">
                    <img
                      style={{
                        width: "100%",
                      }}
                      src={"img/LearnWareLoggo.png"}
                      className="img-avatar"
                      alt="LearnWARE logga in"
                    />
                  </div>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle
                      className="h3"
                      style={{ marginBottom: "0.25rem" }}
                    >
                      <FormattedMessage
                        id={
                          props.activation
                            ? "Views.ResetPassword.ResetPasswordForm.ActivationHeader"
                            : "Views.ResetPassword.ResetPasswordForm.Header"
                        }
                      />
                      <i
                        onClick={redirectUrl}
                        title="Tillbaka till loginsidan"
                        style={{ cursor: "pointer" }}
                        className="fa fa-close float-end close"
                      />
                    </CardTitle>
                  </CardHeader>

                  {status === "sent" ? (
                    <CardBody>
                      <p>
                        <FormattedMessage id="Views.ResetPassword.ResetPasswordForm.Success" />
                      </p>
                      <p className="text-muted">
                        <FormattedMessage
                          id="Views.ResetPassword.ResetPasswordForm.SentTo"
                          values={{ email: values.email }}
                        />
                      </p>
                      <Button
                        type="button"
                        color="success"
                        block
                        onClick={redirectUrl}
                      >
                        <FormattedMessage id="Views.ResetPassword.ResetPasswordForm.ToLogin" />
                      </Button>
                      <Button
                        type="button"
                        color="link"
                        block
                        onClick={() => setStatus(null)}
                      >
                        <FormattedMessage id="Views.ResetPassword.ResetPasswordForm.TryAnotherAddress" />
                      </Button>
                    </CardBody>
                  ) : (
                    <CardBody>
                      <FormGroup>
                        <Label>
                          <FormattedMessage
                            id={
                              props.activation
                                ? "Views.ResetPassword.ResetPasswordForm.ActivationBody"
                                : "Views.ResetPassword.ResetPasswordForm.Body"
                            }
                          />
                        </Label>
                        <Field
                          autoFocus={true}
                          className={`form-control ${
                            errors.email && touched.email && "is-invalid"
                          }`}
                          type="email"
                          name="email"
                          autoComplete="username"
                          readOnly={isFetchingEmail}
                          required
                          placeholder="Ange din e-post"
                        />
                        {errors.email && touched.email && (
                          <div className="invalid-feedback">{errors.email}</div>
                        )}
                      </FormGroup>

                      <Button
                        type="submit"
                        color="success"
                        block
                        disabled={isSubmitting || isFetchingEmail}
                      >
                        {isSubmitting ? (
                          <FormattedMessage id="Views.ResetPassword.ResetPasswordForm.Label.ButtonPressed" />
                        ) : (
                          <FormattedMessage id="Views.ResetPassword.ResetPasswordForm.Label.Button" />
                        )}
                      </Button>
                    </CardBody>
                  )}
                </Card>
              </CardGroup>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const FormikForm = withFormik({
  mapPropsToValues({ email }) {
    return {
      email: email || "",
    };
  },

  handleSubmit(values, { props, setErrors, setStatus, setSubmitting }) {
    axios
      .post(`${API_BASE_URL}/password/resetpassword`, {
        email: values.email,
        isFromApp: Boolean(props.isFromApp),
      })
      .then(() => {
        setStatus("sent");
      })
      .catch((err) => {
        setErrors({
          email:
            err.response?.status === 429 ? (
              <FormattedMessage id="TooManyRequestsTryLater" />
            ) : (
              formatServiceError(props.intl, err?.response?.data)
            ),
        });
      })
      .finally(() => setSubmitting(false));
  },
})(RequestResetPasswordForm);

const RequestResetPasswordFormWithIntl = (props) => {
  const intl = useIntl();
  const location = useLocation();

  return (
    <FormikForm
      {...props}
      intl={intl}
      legacyCheck={location.state?.legacyCheck}
    />
  );
};

export default RequestResetPasswordFormWithIntl;
